import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './DatabaseResetPage.css';
import { clearSubscriptionStatus } from '../utils/stripeService';

const DatabaseResetPage = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const handleDeleteStorage = async () => {
    if (confirmation !== 'DELETE_ALL_STORAGE_CONFIRM') {
      setError('Please type "DELETE_ALL_STORAGE_CONFIRM" to proceed');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetch('/.netlify/functions/delete-all-storage', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ confirmation }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete storage');
      }

      setResult({
        success: true,
        message: 'Storage cleanup completed successfully',
        details: data,
      });
    } catch (err) {
      console.error('Error deleting storage:', err);
      setError(err.message || 'An error occurred while deleting storage');
      setResult({
        success: false,
        message: 'Storage cleanup failed',
        error: err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate('/dashboard');
  };

  const handleClearLocalSubscriptions = () => {
    try {
      // Clear all subscription-related items from localStorage
      const keys = Object.keys(localStorage);
      let count = 0;
      
      for (const key of keys) {
        if (key.includes('userSubscription')) {
          localStorage.removeItem(key);
          count++;
        }
      }
      
      setResult({
        success: true,
        message: `Local subscription data cleared successfully`,
        details: { clearedItems: count }
      });
    } catch (err) {
      console.error('Error clearing local subscription data:', err);
      setError(err.message || 'An error occurred while clearing local subscription data');
    }
  };

  const handleToggleResetMode = async () => {
    try {
      // This is just a UI toggle - the actual reset mode is controlled by the SUBSCRIPTION_RESET_MODE
      // environment variable in netlify.toml or the Netlify dashboard
      setResult({
        success: true,
        message: `Subscription Reset Mode`,
        details: { 
          note: "To enable Subscription Reset Mode, set SUBSCRIPTION_RESET_MODE=true in your Netlify environment variables.",
          currentSetting: "Check your Netlify dashboard under Environment variables to see the current setting."
        }
      });
    } catch (err) {
      console.error('Error toggling reset mode:', err);
      setError(err.message || 'An error occurred while toggling reset mode');
    }
  };

  return (
    <div className="database-reset-page">
      <div className="database-reset-container">
        <h1>Database Reset Utility</h1>
        
        <div className="warning-box">
          <h2>⚠️ WARNING ⚠️</h2>
          <p>This utility will permanently delete ALL user data, documents, and files from the database.</p>
          <p>This action cannot be undone. Make sure you have backups if needed.</p>
        </div>

        <div className="instructions">
          <h3>Instructions:</h3>
          <ol>
            <li>
              <strong>Step 1:</strong> Run the SQL script in the Supabase SQL Editor:
              <pre className="code-block">
                {`-- Disable triggers temporarily to avoid issues with RLS policies
SET session_replication_role = 'replica';

-- Delete data from tables with foreign key dependencies first
TRUNCATE TABLE shared_documents CASCADE;
TRUNCATE TABLE documents CASCADE;
TRUNCATE TABLE signatures CASCADE;
DO $$
BEGIN
    IF EXISTS (SELECT FROM information_schema.tables WHERE table_name = 'subscriptions') THEN
        TRUNCATE TABLE subscriptions CASCADE;
    END IF;
END
$$;
TRUNCATE TABLE user_profiles CASCADE;
TRUNCATE TABLE auth.users CASCADE;

-- Re-enable triggers
SET session_replication_role = 'origin';`}
              </pre>
            </li>
            <li>
              <strong>Step 2:</strong> Delete all storage objects using the button below:
              <div className="confirmation-input">
                <p>Type <code>DELETE_ALL_STORAGE_CONFIRM</code> to confirm:</p>
                <input
                  type="text"
                  value={confirmation}
                  onChange={(e) => setConfirmation(e.target.value)}
                  placeholder="DELETE_ALL_STORAGE_CONFIRM"
                />
              </div>
              <button
                className="delete-button"
                onClick={handleDeleteStorage}
                disabled={loading}
              >
                {loading ? 'Deleting...' : 'Delete All Storage Objects'}
              </button>
              {error && <div className="error-message">{error}</div>}
            </li>
          </ol>
        </div>

        <div className="additional-actions">
          <h3>Additional Actions:</h3>
          <div className="action-item">
            <p>Clear local subscription data (browser cache):</p>
            <button 
              className="action-button"
              onClick={handleClearLocalSubscriptions}
            >
              Clear Local Subscription Data
            </button>
            <p className="action-description">
              Use this if you still see subscription status after database reset.
              This clears cached subscription data from your browser.
            </p>
          </div>
          
          <div className="action-item">
            <p>Subscription Reset Mode Information:</p>
            <button 
              className="action-button info-button"
              onClick={handleToggleResetMode}
            >
              Subscription Reset Mode Info
            </button>
            <p className="action-description">
              View information about Subscription Reset Mode, which prevents checking Stripe
              for subscription status after a database reset.
            </p>
          </div>
        </div>

        {result && (
          <div className={`result-box ${result.success ? 'success' : 'error'}`}>
            <h3>{result.message}</h3>
            <pre>{JSON.stringify(result.details, null, 2)}</pre>
          </div>
        )}

        <button className="back-button" onClick={handleGoBack}>
          Back to Dashboard
        </button>
      </div>
    </div>
  );
};

export default DatabaseResetPage;
